import React, { useCallback, useEffect, useState } from 'react';
import { useVerifyQuery } from 'generated/graphql';
import Cookies from 'js-cookie';
import {
  CookieNames,
  CookieStateValues,
  HOURS_TO_REJECT,
  MS_IN_HOUR,
} from 'constants/cookies';
import { createPortal } from 'react-dom';
import { statApi } from 'redux/reducers/stat';
import {
  ButtonsWrapper, Container, Text, Wrapper, Button,
} from './PPPortal.styled';

const getCookiePP = () => {
  const cookieState = Cookies.get(CookieNames.PP_STATE);
  const cookieDate = Cookies.get(CookieNames.PP_DATE);

  return [cookieState, cookieDate];
};

const parseCookieDate = (cookieDate?: string) => (cookieDate
  ? Number(String(cookieDate).trim()) || 0 : 0);

function PPPortal({ policyText }: { policyText: string }) {
  const [showBanner, setShowBanner] = useState(false);
  const { useSendStatsMutation, useLazyCheckStatusQuery } = statApi;
  const { data: verifyData, loading: verifyLoading } = useVerifyQuery();
  const [sendStats] = useSendStatsMutation();
  const [checkStats, { isLoading: statsLoading }] = useLazyCheckStatusQuery();
  const portalEl = document.querySelector('#pp-portal');

  const onPPBtnClick = useCallback((state: boolean) => {
    const cookiesSet = () => {
      Cookies.set(
        CookieNames.PP_STATE,
        state
          ? CookieStateValues.accepted
          : CookieStateValues.rejected,
      );
      Cookies.set(CookieNames.PP_DATE, Date.now().toString());
    };

    setShowBanner(false);

    cookiesSet();

    if (verifyData?.user) {
      sendStats({
        msisdn: verifyData.user.msisdn,
        accepted: state,
        timestamp: Date.now(),
      })
        .then((res) => {
          if ('error' in res) cookiesSet();
        });
    }
  }, [verifyData]);

  const checkWithCookies = useCallback(() => {
    const [cookieState, cookieDate] = getCookiePP();

    if (!cookieState) {
      setShowBanner(true);
      return;
    }

    if (cookieState === CookieStateValues.accepted) {
      setShowBanner(false);
      return;
    }

    if (cookieState === CookieStateValues.rejected) {
      if (!cookieDate) {
        setShowBanner(true);
        return;
      }

      const savedDate = parseCookieDate(cookieDate);
      const hoursPassed = (Date.now() - savedDate) / MS_IN_HOUR;
      setShowBanner(hoursPassed > HOURS_TO_REJECT);
      return;
    }

    setShowBanner(true);
  }, []);

  useEffect(() => {
    if (!verifyData?.user) return;

    const [cookieState, cookieDate] = getCookiePP();

    if (cookieState && cookieDate) {
      sendStats({
        msisdn: verifyData.user.msisdn,
        accepted: Number(cookieState) === 1,
        timestamp: parseCookieDate(cookieDate),
      });
    }
  }, [verifyData]);

  useEffect(() => {
    if (verifyLoading) return;

    if (!verifyData?.user) {
      checkWithCookies();

      return;
    }

    checkStats({ msisdn: verifyData.user.msisdn })
      .then((res) => {
        if ('data' in res && res.data) {
          setShowBanner(res.data.show_banner);
        } else {
          checkWithCookies();
        }
      }).catch(() => {
        checkWithCookies();
      });
  }, [verifyData, verifyLoading]);

  if (statsLoading || !showBanner) return null;

  return portalEl ? createPortal(
    <Container>
      <Wrapper>
        <Text dangerouslySetInnerHTML={{ __html: policyText }} />
        <ButtonsWrapper>
          <Button $reject onClick={() => onPPBtnClick(false)}>Не принимаю</Button>
          <Button onClick={() => onPPBtnClick(true)}>Принимаю</Button>
        </ButtonsWrapper>
      </Wrapper>
    </Container>,
    portalEl,
  ) : null;
}

export default PPPortal;
